
import { useState } from 'react';
import { Play, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AnimatedCounter from '@/components/AnimatedCounter';

interface HeroSectionV3Props {
  onPhoneClick: () => void;
}

const HeroSectionV3 = ({ onPhoneClick }: HeroSectionV3Props) => {
  const [hasClicked, setHasClicked] = useState(false);

  const handlePhoneClick = () => {
    setHasClicked(true);
    onPhoneClick();
    setTimeout(() => setHasClicked(false), 2500);
  };

  const scrollToWaitlist = () => {
    const waitlistSection = document.querySelector('#waitlist-section');
    if (waitlistSection) {
      waitlistSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToPACTSection = () => {
    const pactSection = document.querySelector('#what-is-pact');
    if (pactSection) {
      pactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="container max-w-7xl mx-auto px-4 py-12 relative">
      <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10">
        {/* Left side - Enhanced messaging */}
        <div className="text-left lg:pr-8">
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-white mb-4 leading-[0.85] tracking-tighter">
            What comes
            <span className="block text-yellow-400 mt-1">before</span>
            <span className="block mt-1">habits & goals</span>
          </h1>
          
          {/* Show up every day tagline */}
          <div className="mb-6">
            <h2 className="text-3xl md:text-4xl font-black text-yellow-400 mb-2">
              Show up every day
            </h2>
            <p className="text-lg text-white/80 font-medium">
              (even when it's hard)
            </p>
          </div>
          
          <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed font-bold">
            Don't overcomplicate it. Make a simple commitment for a set time.
            <br />
            <span className="text-yellow-400">Show Up! is your daily buddy who helps you experiment with life through PACTs</span>
            <br />
            - not forever habits. Show up every day, even when it's hard.
          </p>

          {/* PACT Formula Highlight */}
          <div className="bg-yellow-400/20 backdrop-blur-sm rounded-3xl p-8 mb-10 border border-yellow-400/30">
            <h3 className="text-white font-black text-2xl mb-4">The PACT Formula:</h3>
            <div className="text-yellow-400 font-black text-3xl mb-4">
              "I will [action] for [duration]"
            </div>
            <div className="space-y-2 text-white/90 font-medium">
              <p>• "I will read 10 pages for 21 days"</p>
              <p>• "I will walk 15 minutes for 14 days"</p>
              <p>• "I will meditate 5 minutes for 30 days"</p>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <Button
              onClick={scrollToWaitlist}
              className="bg-yellow-400 text-purple-800 hover:bg-yellow-300 rounded-2xl px-8 py-4 font-black text-lg"
            >
              Start Your First PACT
            </Button>
            <Button
              onClick={scrollToPACTSection}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 rounded-2xl px-8 py-4 font-bold text-lg"
            >
              Learn About PACTs
            </Button>
          </div>

          {/* Enhanced Social Proof */}
          <div className="text-white">
            <div className="text-4xl md:text-5xl font-black mb-4">
              <AnimatedCounter target={847} className="text-yellow-400" />
              <span className="text-white text-3xl">+</span>
            </div>
            <p className="text-xl text-white/90 font-bold mb-2">
              people showing up every day with PACTs instead of perfect habits
            </p>
            <p className="text-lg text-white/70 font-medium">
              "Finally, an app that gets it. Life is hard, but showing up matters." - Sarah M.
            </p>
            <div className="w-24 h-2 bg-yellow-400/60 rounded-full mt-4"></div>
          </div>
        </div>

        {/* Right side - Enhanced Interactive Phone Mockup */}
        <div className="flex justify-center lg:justify-end">
          <div className="relative group">
            {/* iPhone Frame with tilt */}
            <div className="w-[320px] h-[640px] bg-black rounded-[3.5rem] p-4 shadow-2xl transform rotate-12 hover:rotate-6 transition-all duration-700 hover:scale-105">
              <div className="w-full h-full bg-white rounded-[3rem] overflow-hidden relative">
                {/* Status Bar */}
                <div className="h-12 bg-gray-50 flex items-center justify-between px-8 text-sm font-semibold border-b border-gray-100">
                  <span className="text-black">9:41</span>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-1 h-1 bg-black rounded-full"></div>
                      <div className="w-1 h-1 bg-black rounded-full"></div>
                      <div className="w-1 h-1 bg-black rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                    </div>
                    <div className="w-6 h-3 bg-green-500 rounded-sm"></div>
                  </div>
                </div>
                
                {/* App Content - PACT Creation Interface */}
                <div className="p-8 h-full bg-gradient-to-b from-purple-50 via-white to-purple-50/30">
                  <div className="mb-6">
                    <h2 className="text-2xl font-black text-purple-700 mb-2">Hey there! 👋</h2>
                    <p className="text-purple-600 font-medium text-sm">Ready to show up every day?</p>
                  </div>
                  
                  <div className="bg-white rounded-3xl p-6 shadow-lg border border-purple-100 mb-6">
                    <div className="mb-4">
                      <label className="text-sm font-bold text-purple-800 mb-2 block">I will...</label>
                      <div className="bg-gray-50 rounded-2xl p-4 text-purple-800 font-medium">
                        Exercise for 30 minutes
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="text-sm font-bold text-purple-800 mb-2 block">For...</label>
                      <div className="bg-yellow-50 rounded-2xl p-4 text-purple-800 font-bold border-2 border-yellow-400">
                        21 days
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-purple-600 mb-4">
                      <FlaskConical size={16} />
                      <span className="font-medium">This is an experiment</span>
                    </div>

                    <div className="bg-yellow-400/10 rounded-2xl p-3 border border-yellow-400/20">
                      <p className="text-xs text-purple-700 font-medium">
                        💪 "Show up every day - I've got your back!" - Your AI Buddy
                      </p>
                    </div>
                  </div>
                  
                  {/* Interactive Button */}
                  <button
                    onClick={handlePhoneClick}
                    disabled={hasClicked}
                    className={`w-full py-4 rounded-3xl font-black text-lg transition-all duration-500 transform ${
                      hasClicked 
                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white scale-95' 
                        : 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {hasClicked ? '🎉 Let\'s show up!' : 'Create My PACT'}
                  </button>
                  
                  {!hasClicked && (
                    <div className="flex items-center justify-center mt-4 text-purple-600 animate-bounce">
                      <Play size={16} className="mr-2" />
                      <span className="text-xs font-semibold">Tap to start showing up</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Floating elements around the phone */}
            <div className="absolute -top-16 -left-16 w-28 h-28 bg-yellow-400/40 rounded-full blur-xl animate-pulse-gentle"></div>
            <div className="absolute -bottom-20 -right-20 w-36 h-36 bg-purple-400/30 rounded-full blur-2xl animate-pulse-gentle delay-1000"></div>
            <div className="absolute top-1/4 -right-12 w-20 h-20 bg-white/40 rounded-full blur-lg animate-pulse-gentle delay-500"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSectionV3;
